<template>
  <div v-if="showSpinner" class="credit-cards">
    <LoadingSpinner />
  </div>

  <div v-else class="add-credit-card">
    <AddPaymentMethodComponent
      :form-submit-button-text="tn('next')"
      @submit="submit()"
      @payment-method-added="zuoraCreditCardFormSuccessHandler"
      @payment-method-error="zuoraCreditCardFormFailureHandler"
    >
      <template #cancelButton>
        <button id="btnCancel" type="button" class="btn-outline-primary cancel" @click="cancel()">
          {{ tn("cancel") }}
        </button>
      </template>
    </AddPaymentMethodComponent>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, onMounted, onUnmounted, ref } from "vue";
import { logEvent, logException } from "@/common/logger";
import { t } from "@/i18n";
import { unifiedApi } from "@/common";
import AddPaymentMethodComponent from "@/components/shared/AddPaymentMethodComponent.vue";
import { ISubscription } from "@/common/api/unifiedPortal/interfaces";
import { NotificationType, useNotificationsStore } from "@/stores/notifications";
import { pageState } from "@/globalEnums";
import LoadingSpinner from "@/components/shared/LoadingSpinner.vue";
import { handleApiError } from "@/common/handleApiError";
import { AxiosError } from "axios";
import { RateLimitCreditCardError } from "@/define";

const componentName = "AddCreditCardComponent";

export default defineComponent({
  name: componentName,
  components: {
    AddPaymentMethodComponent,
    LoadingSpinner,
  },
  props: {
    sub: {
      type: Object as PropType<ISubscription>,
      default: null,
    },
    creditCardPreviousState: {
      type: String as PropType<pageState>,
      default: null,
    },
  },
  emits: ["payment-method-added", "subscription-page-state-change"],

  setup(props, context) {
    logEvent("created", componentName);
    const notificationsStore = useNotificationsStore();

    const showSpinner = ref<boolean>(true);

    function tn(v: string, params?: Record<string, unknown>): string {
      return t(`${componentName}.${v}`, params);
    }

    onMounted(async () => {
      try {
        logEvent("mounted", componentName);
        showSpinner.value = false;
      } catch (err) {
        if ((err as string) === RateLimitCreditCardError) {
        } else {
          //Need to change to logException because this catch was overwriting the one from creditCardFunctions.getCreditCardFormParams
          //where we are putting up the notification instead of redirecting
          logException(err as Error, componentName);
        }
      }
    });

    onUnmounted(() => {
      logEvent("unmounted", componentName);
    });

    async function zuoraCreditCardFormSuccessHandler(event) {
      try {
        await setNewDefaultCC(event.detail.creditCardId);
        context.emit("payment-method-added", props.sub);
      } catch (err) {
        handleApiError(err as AxiosError);
      }
    }

    async function zuoraCreditCardFormFailureHandler(error: string) {
      if (error === "AddCreditCardFailed") {
        notificationsStore.clearNotifications();
        notificationsStore.addNotification({ type: error as NotificationType });
      }
    }

    async function setNewDefaultCC(creditCardId: string) {
      try {
        await unifiedApi.setDefaultCreditCard(creditCardId);
      } catch (err) {
        handleApiError(err as AxiosError);
      }
    }

    function submit() {
      logEvent("Submit Zuora Credit Card Form");
      /* eslint-disable */
      //@ts-ignore
      Z.submit();
      /* eslint-enable */
    }

    function cancel() {
      context.emit("subscription-page-state-change", props.creditCardPreviousState);
    }

    //Expose to template
    return {
      tn,
      showSpinner,
      cancel,
      submit,
      zuoraCreditCardFormSuccessHandler,
      zuoraCreditCardFormFailureHandler,
    };
  },
});
</script>

<style scoped lang="css">
@import "@/styles/variables.css";
button {
  margin: var(--space-quarter);
  text-align: center;
}
.btn {
  margin-left: 112px;
}
.btn .spinning-icon {
  display: none;
}
.btn:disabled {
  background-color: #c0cea0;
  cursor: not-allowed;
}
.btn:disabled .spinning-icon {
  display: inline;
}
</style>
